/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import ArgonButton from "components/ArgonButton";
import { Chip } from "@mui/material";

// Data
import CreditMappingViewTableData from "Views/Activities/CreditMapping/data/creditMappingViewTableData";
import CreditMappingViewCreditTableData from "Views/Activities/CreditMapping/data/creditMappingViewCreditTableData";

// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import { useState, useEffect } from "react";
import { set } from "date-fns";
import Dropdown from "components/CustomDropdown/Dropdown";
import ArgonInput from "components/ArgonInput";
import { useParams, useNavigate } from "react-router-dom";
import api from "utils/api";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PropTypes from "prop-types";

function Tables({ classData }) {
  const [cycle, setCycle] = useState("");

  // Calculate progress - use completedClasses and totalNoOfClasses from classData
  const completedSessions = classData?.completedClasses || 0;
  const totalSessions = classData?.totalNoOfClasses || classData?.totalClasses || 0;
  const progress = totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0;

  // Calculate hourly rates
  // hourlyRate = totalAmount / totalHours
  const totalMinutes =
    (classData?.totalNoOfClasses || classData?.totalClasses || 0) * (classData?.duration || 90);
  const hourlyRate =
    totalMinutes > 0 && classData?.paymentAmount
      ? Math.round(classData.paymentAmount / (totalMinutes / 60))
      : classData?.feeCharge || 0;

  const tutorHourlyRate = classData?.tutorPayRate || classData?.tutorSalary || 0;

  // Calculate sessions
  const sessionsPerWeek = classData?.numberOfClassesInAWeek || classData?.weeklyClasses || 0;
  const hoursPerSession = classData?.duration ? classData.duration / 60 : 1.5;
  const totalClasses = classData?.totalNoOfClasses || classData?.totalClasses || 0;
  const noOfMonths = classData?.noOfMonths || 1;
  const sessionsPerMonth = noOfMonths > 0 ? Math.round(totalClasses / noOfMonths) : 0;

  // Calculate used amount
  const usedAmount =
    totalSessions > 0
      ? Math.floor((completedSessions / totalSessions) * (classData?.paymentAmount || 0))
      : 0;

  // Get class status
  const classStatus = classData?.status || "N/A";

  // Get tutor name
  const tutorName = classData?.tutor?.fullName || classData?.tutor?.name || "N/A";

  // Get student data for table
  const studentsData = classData?.students || [];

  const { columns } = CreditMappingViewTableData;

  // Generate dynamic rows from studentsData
  const rows = studentsData.map((student, index) => ({
    Sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {index + 1}
      </ArgonTypography>
    ),
    "Student name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.name || student?.fullName || "N/A"}
      </ArgonTypography>
    ),
    subject: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.subjects && student.subjects.length > 0
          ? student.subjects.map((sub) => sub.name || sub).join(", ")
          : "N/A"}
      </ArgonTypography>
    ),
    Gender: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.gender || "N/A"}
      </ArgonTypography>
    ),
    "Date of Birth": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.dob || student?.dateOfBirth
          ? new Date(student.dob || student.dateOfBirth).toLocaleDateString()
          : "N/A"}
      </ArgonTypography>
    ),
    "School/college/university Name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.board?.name || student?.schoolName || "N/A"}
      </ArgonTypography>
    ),
    "Preferred Time": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.preferredTimeDuration
          ? `${student.preferredTimeDuration} mins`
          : student?.preferredTime || "N/A"}
      </ArgonTypography>
    ),
    "Tuition Tenure": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.noOfClassesPerWeek
          ? `${student.noOfClassesPerWeek} classes/week`
          : student?.tuitionTenure || "N/A"}
      </ArgonTypography>
    ),
    "Location City": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.parent?.city || student?.locationCity || classData?.parent?.city || "N/A"}
      </ArgonTypography>
    ),
    "Tutors Level": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {classData?.tutor?.tutorLevel || student?.tutorLevel || "N/A"}
      </ArgonTypography>
    ),
  }));

  const { columns: columns1 } = CreditMappingViewCreditTableData;

  // Generate dynamic rows for Credit Details from cycles
  const cycles = classData?.cycles || [];
  const rows1 = cycles.map((cycle, index) => ({
    "Cycle Name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.name || `Cycle ${index + 1}`}
      </ArgonTypography>
    ),
    "Total Classes": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.totalNoOfClasses || 0}
      </ArgonTypography>
    ),
    "Completed Classes": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.completedClasses || 0}
      </ArgonTypography>
    ),
    Status: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.status || "N/A"}
      </ArgonTypography>
    ),
  }));

  return (
    <ArgonBox py={3}>
      <ArgonBox mb={3}>
        <Card>
          <ArgonBox p={3}>
            <Card
              sx={{
                mt: 3,
                borderRadius: "20px",
                backgroundColor: "white",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                border: "1px solid #f5f5f5",
              }}
            >
              <ArgonBox px={3} py={1.5}>
                {" "}
                {/* 👈 yaha py kam kiya */}
                <ArgonBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                  gap={4}
                >
                  {/* Class Status */}
                  <ArgonBox display="flex" alignItems="center" flexDirection="row">
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                      Class Status
                    </ArgonTypography>
                    <ArgonBox
                      ml={2}
                      px={2.5}
                      py={0.5}
                      sx={{
                        background:
                          classStatus === "CREATED"
                            ? "linear-gradient(135deg, #9e9e9e, #757575)"
                            : classStatus === "SCHEDULED"
                            ? "linear-gradient(135deg, #2196f3, #1976d2)"
                            : classStatus === "ONGOING"
                            ? "linear-gradient(135deg, #ff9800, #f57c00)"
                            : classStatus === "CANCELLED"
                            ? "linear-gradient(135deg, #f44336, #d32f2f)"
                            : classStatus === "COMPLETED"
                            ? "linear-gradient(135deg, #43a047, #26a69a)"
                            : classStatus === "ON_HOLD"
                            ? "linear-gradient(135deg, #ff5722, #e64a19)"
                            : "linear-gradient(135deg, #f44336, #d32f2f)",
                        borderRadius: "20px",
                        boxShadow: "0 4px 10px rgba(67,160,71,0.3)",
                      }}
                    >
                      <ArgonTypography
                        variant="caption"
                        fontWeight="bold"
                        color="white"
                        sx={{ fontSize: "13px", letterSpacing: "0.5px" }}
                      >
                        {classStatus}
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>

                  {/* Tutor Name */}
                  <ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium" color="text">
                      Tutor&apos;s Name
                    </ArgonTypography>
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                      {tutorName}
                    </ArgonTypography>
                  </ArgonBox>

                  {/* Cycle Dropdown */}
                  <ArgonBox display="flex" alignItems="center" flexDirection="row">
                    <ArgonTypography variant="body2" fontWeight="medium" mr={2}>
                      Cycle
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      options={cycles.map((cycle, index) => cycle.name || `Cycle ${index + 1}`)}
                      value={cycle}
                      onChange={(e) => setCycle(e.target.value)}
                      sx={{
                        minWidth: "100px",
                        borderRadius: "12px",
                        backgroundColor: "#fafafa",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      }}
                    />
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </Card>
          </ArgonBox>

          <ArgonBox>
            <ArgonTypography sx={{ mx: 2, mb: 3 }} variant="h5" fontWeight="medium">
              Students List
            </ArgonTypography>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={columns} rows={rows} />
            </ArgonBox>
          </ArgonBox>

          <Card
            sx={{
              mt: 3,
              borderRadius: "16px",
              backgroundColor: "white",
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
            }}
          >
            <ArgonBox p={3}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                <ArgonBox
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    p: 1.2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  {/* <Payment sx={{ color: "#344767", fontSize: "22px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                    Package Details
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text">
                    Pricing and session information
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <Grid container spacing={3}>
                {/* Hourly Rates */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <AttachMoney sx={{ color: "#4caf50", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Hourly Rates
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Fee Charge per hour
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                          ₹ {hourlyRate}
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Tutor Salary
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                          ₹ {tutorHourlyRate}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Sessions */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <Schedule sx={{ color: "#2196f3", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Sessions
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Sessions in a week
                        </ArgonTypography>
                        <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                          {sessionsPerWeek} sessions | {sessionsPerWeek * hoursPerSession} hours
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Sessions in a month
                        </ArgonTypography>
                        <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                          {sessionsPerMonth} sessions | {sessionsPerMonth * hoursPerSession} hours
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Monthly Summary */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <Payment sx={{ color: "#344767", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Monthly Summary
                      </ArgonTypography>
                    </ArgonBox>

                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Charge
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                            ₹ {classData?.paymentAmount || 0}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Pay
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                            ₹ {classData?.monthlyTutorSalary || 0}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Profit
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                            ₹{" "}
                            {(classData?.paymentAmount || 0) - (classData?.monthlyTutorSalary || 0)}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                    </Grid>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Tutor Info */}
              <ArgonBox mt={3}>
                <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                  Tutor Name - {tutorName}
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
          </Card>

          <Card
            sx={{
              mt: 3,
              borderRadius: "20px",
              backgroundColor: "white",
              boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative Background Element */}
            <ArgonBox
              sx={{
                position: "absolute",
                top: -40,
                right: -40,
                width: 120,
                height: 120,
                background: "linear-gradient(45deg, #667eea, #764ba2)",
                borderRadius: "50%",
                opacity: 0.05,
              }}
            />

            <ArgonBox p={4}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" gap={2} mb={4}>
                <ArgonBox
                  sx={{
                    background: "linear-gradient(45deg, #667eea, #764ba2)",
                    borderRadius: "12px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  {/* <AccountBalance sx={{ color: "white", fontSize: "22px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                    Credit Counter
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#6c757d">
                    Track credits, usage and payments
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <Grid container spacing={4}>
                {/* Progress Bar Section */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "#f8f9fa",
                      borderRadius: "16px",
                      p: 3,
                      textAlign: "center",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      justifyContent="center"
                    >
                      {/* <DonutLarge sx={{ color: "#2196F3", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                        Usage Progress
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox
                      sx={{
                        width: 180,
                        height: 180,
                        margin: "0 auto",
                        mb: 2,
                      }}
                    >
                      <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
                        <path
                          d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                          strokeWidth="6"
                          fillOpacity="0"
                          style={{ stroke: "#e0e0e0" }}
                        />
                        <path
                          d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                          strokeWidth="6"
                          fillOpacity="0"
                          style={{
                            stroke: "url(#gradient)",
                            strokeDasharray: "289.027px, 289.027px",
                            strokeDashoffset: "280.356px",
                            strokeLinecap: "round",
                          }}
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" style={{ stopColor: "#667eea", stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: "#764ba2", stopOpacity: 1 }} />
                          </linearGradient>
                        </defs>
                        <text
                          x="50"
                          y="45"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{ fill: "#2c3e50", fontSize: "12px", fontWeight: "bold" }}
                        >
                          {Math.round(progress)}%
                        </text>
                        <text
                          x="50"
                          y="58"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{ fill: "#6c757d", fontSize: "6px" }}
                        >
                          Used
                        </text>
                      </svg>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                        ₹{usedAmount} / ₹{classData?.paymentAmount || 0}
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="#6c757d">
                        Credit Usage
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Table Section */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "white",
                      borderRadius: "16px",
                      p: 3,
                      boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={3}>
                      {/* <TableChart sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                        Credit Details
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox
                      sx={{
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                      }}
                    >
                      <Table columns={columns1} rows={rows1} />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Balance Info Cards */}
              <Grid container spacing={3} mt={2}>
                {/* Left Balance Card */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "linear-gradient(135deg, #e3f2fd 0%, #e2eaf0ff 100%)",
                      borderRadius: "16px",
                      p: 3,
                      border: "1px solid #e1f5fe",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <AccountBalanceWallet sx={{ color: "#1976d2", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#1976d2">
                        Account Summary
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                          Balance:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="#0d47a1">
                          ₹{(classData?.paymentAmount || 0) - usedAmount}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                          Unallocated Sessions:
                        </ArgonTypography>
                        <Chip
                          label={totalSessions - completedSessions}
                          size="small"
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                          Payment Method:
                        </ArgonTypography>
                        <Chip
                          label={classData?.paymentType || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: "#4caf50",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Right Balance Card */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "linear-gradient(135deg, #e8f5e8 0%, #e0ede1ff 100%)",
                      borderRadius: "16px",
                      p: 3,
                      border: "1px solid #e0f2e0",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <Payment sx={{ color: "#388e3c", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#388e3c">
                        Payment Details
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                          Available Balance:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="#1b5e20">
                          ₹{(classData?.paymentAmount || 0) - usedAmount}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                          Payment Status:
                        </ArgonTypography>
                        <Chip
                          label={classData?.paymentStatus || "PENDING"}
                          size="small"
                          sx={{
                            bgcolor: classData?.paymentStatus === "PAID" ? "#4caf50" : "#ff9800",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                          Total Amount:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="#1b5e20">
                          ₹{classData?.paymentAmount || 0}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>
            </ArgonBox>
          </Card>
        </Card>
      </ArgonBox>
    </ArgonBox>
  );
}

Tables.propTypes = {
  classData: PropTypes.shape({
    completedClasses: PropTypes.number,
    totalClasses: PropTypes.number,
    totalNoOfClasses: PropTypes.number,
    feeCharge: PropTypes.number,
    tutorSalary: PropTypes.number,
    tutorPayRate: PropTypes.number,
    weeklyClasses: PropTypes.number,
    numberOfClassesInAWeek: PropTypes.number,
    duration: PropTypes.number,
    noOfMonths: PropTypes.number,
    paymentAmount: PropTypes.number,
    status: PropTypes.string,
    paymentStatus: PropTypes.string,
    paymentType: PropTypes.string,
    monthlyTutorSalary: PropTypes.number,
    tutor: PropTypes.shape({
      name: PropTypes.string,
      fullName: PropTypes.string,
      tutorLevel: PropTypes.string,
    }),
    parent: PropTypes.shape({
      city: PropTypes.string,
    }),
    students: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        subjects: PropTypes.arrayOf(PropTypes.string),
        gender: PropTypes.string,
        dateOfBirth: PropTypes.string,
        schoolName: PropTypes.string,
        preferredTime: PropTypes.string,
        tuitionTenure: PropTypes.string,
        locationCity: PropTypes.string,
        tutorLevel: PropTypes.string,
      })
    ),
    cycles: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        totalNoOfClasses: PropTypes.number,
        completedClasses: PropTypes.number,
        status: PropTypes.string,
      })
    ),
  }),
};

export default Tables;
